import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fetchSettings, updateSettings } from "@/lib/api";

/**
 * Simple admin form to set today's gold/silver rates, shown as a trust-
 * building ticker on the homepage. Leave any field blank to hide that rate
 * from the public ticker (e.g. if you only want to show 22K gold + silver).
 */
export const RatesSettings = () => {
  const [form, setForm] = useState({
    gold_rate_18k: "",
    gold_rate_22k: "",
    gold_rate_24k: "",
    silver_rate_per_gram: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings()
      .then((data) => {
        setForm({
          gold_rate_18k: data.gold_rate_18k ?? "",
          gold_rate_22k: data.gold_rate_22k ?? "",
          gold_rate_24k: data.gold_rate_24k ?? "",
          silver_rate_per_gram: data.silver_rate_per_gram ?? "",
        });
      })
      .catch(() => toast.error("Failed to load current rates"))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      for (const key of Object.keys(form)) {
        payload[key] = form[key] === "" ? null : Number(form[key]);
      }
      await updateSettings(payload);
      toast.success("Rates updated — now live on your homepage");
    } catch {
      toast.error("Failed to update rates");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted-foreground text-sm">Loading current rates...</p>;

  return (
    <div className="max-w-lg" data-testid="admin-rates-settings">
      <div className="mb-6 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4 text-sm text-muted-foreground">
        <strong className="text-foreground">How this works:</strong> Set today&apos;s per-gram rates below — they
        show as a live ticker strip on your homepage. Leave a field blank to hide that specific rate from
        visitors (e.g. if you only quote 22K).
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="rate-18k">18K Gold (₹ per gram)</Label>
          <Input
            id="rate-18k"
            type="number"
            step="0.01"
            min="0"
            value={form.gold_rate_18k}
            onChange={(e) => setForm((f) => ({ ...f, gold_rate_18k: e.target.value }))}
            placeholder="e.g. 5400"
            data-testid="rate-18k-input"
          />
        </div>
        <div>
          <Label htmlFor="rate-22k">22K Gold (₹ per gram)</Label>
          <Input
            id="rate-22k"
            type="number"
            step="0.01"
            min="0"
            value={form.gold_rate_22k}
            onChange={(e) => setForm((f) => ({ ...f, gold_rate_22k: e.target.value }))}
            placeholder="e.g. 6600"
            data-testid="rate-22k-input"
          />
        </div>
        <div>
          <Label htmlFor="rate-24k">24K Gold (₹ per gram)</Label>
          <Input
            id="rate-24k"
            type="number"
            step="0.01"
            min="0"
            value={form.gold_rate_24k}
            onChange={(e) => setForm((f) => ({ ...f, gold_rate_24k: e.target.value }))}
            placeholder="e.g. 7200"
            data-testid="rate-24k-input"
          />
        </div>
        <div>
          <Label htmlFor="rate-silver">Silver (₹ per gram)</Label>
          <Input
            id="rate-silver"
            type="number"
            step="0.01"
            min="0"
            value={form.silver_rate_per_gram}
            onChange={(e) => setForm((f) => ({ ...f, silver_rate_per_gram: e.target.value }))}
            placeholder="e.g. 85"
            data-testid="rate-silver-input"
          />
        </div>

        <Button type="submit" variant="premium" disabled={saving} data-testid="rates-save-btn">
          {saving ? "Saving..." : "Update Rates"}
        </Button>
      </form>
    </div>
  );
};
